import { getFocusStyle } from "../../theme/accessibility";
import type { KivraCSSObject } from "../../theme/styled/styled";
import { calcRemFromPx } from "../../theme/util";
import type { ButtonProps } from "./Button";

export const BaseStyles = (
  props: ButtonProps & { iconOnly: boolean },
): KivraCSSObject => {
  const style: KivraCSSObject = {
    backgroundColor: "transparent",
    transition:
      "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    verticalAlign: "middle",
    justifyContent: "center",
    userSelect: "none",
    WebkitAppearance: "none",
    textDecoration: "none",
    outline: 0,
    width: props.fullWidth ? "100%" : "auto",
    minHeight: props.iconOnly
      ? "44px"
      : props.size === "medium"
      ? "52px"
      : "42px",
    minWidth: props.iconOnly ? "44px" : "100px",
    padding: props.iconOnly
      ? "$spacing-14"
      : props.size === "medium"
      ? ["$spacing-14", "$spacing-24"]
      : ["$spacing-14", "$spacing-16"],
    border: "none",
    borderRadius: "$radius-medium",
    boxSizing: "border-box",
    fontFamily: "$base-font",
    fontSize: props.size === "medium"
      ? `${calcRemFromPx(17)}rem`
      : `${calcRemFromPx(15)}rem`,
    lineHeight: props.size === "medium" ? "1.5rem" : "1rem",
    fontWeight: 600,
    color: "$text-primary",
    cursor: "pointer",
    overflow: "hidden",
    margin: 0,
    ...getFocusStyle(),
  } as const;
  return style;
};

export const DisabledStyles: KivraCSSObject = {
  backgroundColor: "$surface-disabled",
  border: "none",
  textDecoration: "none",
  color: "$text-secondary",
  cursor: "not-allowed",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "$surface-disabled",
    color: "$text-secondary",
    boxShadow: "none",
  },
};

export const PrimaryStyles: KivraCSSObject = {
  backgroundColor: "$button-primary",
  color: "$text-inverted",
  "&:hover": {
    backgroundColor: "$button-primary-hover",
  },
  "&:active": {
    backgroundColor: "$button-primary",
  },
};

export const SecondaryStyles: KivraCSSObject = {
  boxShadow: "inset 0px 0px 0px 1px $button-border",
  color: "$text-primary",
  "&:hover": {
    backgroundColor: "$hover-background",
  },
  "&:active": {
    backgroundColor: "transparent",
  },
};

export const FilledStyles: KivraCSSObject = {
  background: "$background-tertiary",
  boxShadow: "$shadow-raised, inset 0px 0px 0px 1px $beige400",
  fontWeight: 500,
  "&:hover": {
    background: "$button-filled-hover",
    boxShadow: "$shadow-raised, inset 0px 0px 0px 1px $beige500",
  },
  "&:active": {
    background: "$background-tertiary",
    boxShadow: "$shadow-raised, inset 0px 0px 0px 1px $beige400",
  },
};

export const LinkStyles: KivraCSSObject = {
  color: "$text-primary",
  textDecoration: "underline",
  "&:hover": {
    backgroundColor: "$hover-background",
  },
  "&:active": {
    backgroundColor: "transparent",
  },
};

export const DismissiveStyles: KivraCSSObject = {
  color: "$text-white",
  backgroundColor: "$button-dismissive",
  "&:hover": {
    backgroundColor: "$button-dismissive-hover",
  },
  "&:active": {
    backgroundColor: "$button-dismissive",
  },
};
